import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { InvestigationButton, ModalContainer } from "../../components";
import { returnIsValid } from "../../Main/useConstants";
import {
  ACCOUNT_FLOWS,
  ACCOUNT_TYPES,
  emptyAccount,
  normalizeAccount,
  sanitizeAccountNumber,
} from "../../../../../../helpers/financialImpact";
import { AmountField } from "../components";

// Una cuenta sin numero ni titular no sirve para rastrear el dinero.
const validateForm = (account) => {
  const errors = {};

  if (!account.account_number.trim() && !account.holder.trim()) {
    errors.account_number = true;
    errors.holder = true;
  }

  return [Object.keys(errors).length === 0, errors];
};

export const AddAccount = ({
  show,
  onHide,
  accounts,
  setAccounts,
  selectedAccount,
  setSelectedAccount,
  currency,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [account, setAccount] = useState(emptyAccount);
  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  const flowOptions = useMemo(
    () => [
      {
        value: ACCOUNT_FLOWS.AFFECTED,
        label: t("financialImpact.flow_affected"),
      },
      {
        value: ACCOUNT_FLOWS.BENEFICIARY,
        label: t("financialImpact.flow_beneficiary"),
      },
    ],
    [t]
  );

  const accountTypeOptions = useMemo(
    () => [
      {
        value: ACCOUNT_TYPES.ACCOUNT,
        label: t("financialImpact.account_type_account"),
      },
      {
        value: ACCOUNT_TYPES.CLABE,
        label: t("financialImpact.account_type_clabe"),
      },
      { value: ACCOUNT_TYPES.CARD, label: t("financialImpact.account_type_card") },
      {
        value: ACCOUNT_TYPES.OTHER,
        label: t("financialImpact.account_type_other"),
      },
    ],
    [t]
  );

  // Para editar una cuenta existente
  useEffect(() => {
    if (selectedAccount !== null) {
      setAccount(normalizeAccount(accounts[selectedAccount]));
    }
  }, [selectedAccount]);

  // Resetear los valores cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setTimeout(() => {
        resetValues();
      }, 200);
    }
  }, [show]);

  // Validar en cada cambio
  useEffect(() => {
    if (triedToSave) {
      const [, currentErrors] = validateForm(account);
      setErrors(currentErrors);
    }
  }, [account]);

  const handleChange = (field) => (value) =>
    setAccount((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    setTriedToSave(true);
    const [isValid, currentErrors] = validateForm(account);
    setErrors(currentErrors);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("financialImpact.account_needs_number_or_holder"),
      });
      return;
    }

    if (selectedAccount !== null) {
      const newAccounts = _.cloneDeep(accounts);
      newAccounts[selectedAccount] = account;
      setAccounts(newAccounts);
    } else {
      setAccounts([...accounts, account]);
    }
    onHide();
  };

  const resetValues = () => {
    setAccount(emptyAccount());
    setErrors({});
    setTriedToSave(false);
    setSelectedAccount(null);
  };

  return (
    <ModalContainer show={show} onHide={onHide}>
      <Stack spacing={2}>
        {/* Header */}
        <Box>
          <Typography
            fontWeight={700}
            fontSize={28}
            color={theme.palette.ethicsBlue.main}
          >
            {t("financialImpact.account_modal_title")}
          </Typography>
          <Typography fontSize={16} color="oxfordBlue">
            {t("financialImpact.account_modal_description")}
          </Typography>

          <hr style={{ marginBottom: 0 }} />
        </Box>

        <Grid2 container spacing={2}>
          {/* El flujo es lo que responde de donde salio y a donde se fue el dinero */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={`${t("financialImpact.flow")}:`}
              options={flowOptions}
              value={account.flow}
              onChange={(e) => handleChange("flow")(e.target.value)}
              isValid={null}
              showHelpText={false}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={`${t("financialImpact.account_type")}:`}
              options={accountTypeOptions}
              value={account.account_type}
              onChange={(e) => handleChange("account_type")(e.target.value)}
              isValid={null}
              showHelpText={false}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGInput
              label={t("financialImpact.institution")}
              value={account.institution}
              onChange={(e) => handleChange("institution")(e.target.value)}
              showHelpText={false}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGInput
              label={t("financialImpact.account_number")}
              value={account.account_number}
              onChange={(e) =>
                handleChange("account_number")(
                  sanitizeAccountNumber(e.target.value)
                )
              }
              isValid={returnIsValid(errors?.account_number, triedToSave)}
              showHelpText={false}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGInput
              label={t("financialImpact.holder")}
              value={account.holder}
              onChange={(e) => handleChange("holder")(e.target.value)}
              isValid={returnIsValid(errors?.holder, triedToSave)}
              showHelpText={false}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <AmountField
              label={`${t("financialImpact.account_amount")} (${currency})`}
              value={account.amount}
              onChange={handleChange("amount")}
            />
          </Grid2>

          <Grid2 size={12}>
            <EGInput
              label={t("financialImpact.details")}
              value={account.details}
              onChange={(e) => handleChange("details")(e.target.value)}
              multiline
              rows={3}
              showHelpText={false}
            />
          </Grid2>
        </Grid2>

        {/* Botones */}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <InvestigationButton
            label={t("financialImpact.cancel")}
            variant="main"
            outlined
            onClick={onHide}
          />
          <InvestigationButton
            label={t("financialImpact.save")}
            variant="main"
            onClick={handleSave}
          />
        </Stack>
      </Stack>
    </ModalContainer>
  );
};
